import { GitBranch, Stethoscope, CreditCard, FileText, Calendar, Shield } from "lucide-react";
import { cn } from "../../lib/utils";
import type { GraphDefinition } from "../../api/graphs";

interface WfTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  definition: GraphDefinition;
}

const WF_TEMPLATES: WfTemplate[] = [
  {
    id: "billing-pipeline",
    name: "Billing Pipeline",
    description: "End-to-end claim submission: eligibility check, scrub, submit, track status, handle denials",
    icon: CreditCard,
    color: "text-blue-500 bg-blue-100 dark:bg-blue-900/30",
    definition: {
      nodes: [
        { id: "start", type: "wf_start", position: { x: 300, y: 30 }, data: {} },
        { id: "trigger", type: "wf_trigger", position: { x: 260, y: 120 }, data: { name: "New Encounter Closed", triggerType: "clinical_event" } },
        { id: "ehr_fetch", type: "ehr_action", position: { x: 260, y: 230 }, data: { name: "Fetch Encounter", ehrAction: "read", resourceType: "Encounter" } },
        { id: "eligibility", type: "payer_action", position: { x: 260, y: 340 }, data: { name: "Check Eligibility", payerAction: "eligibility" } },
        { id: "decision", type: "decision", position: { x: 260, y: 450 }, data: { name: "Eligible?", conditionExpression: "data.eligible === true" } },
        { id: "scrub_agent", type: "agent_ref", position: { x: 80, y: 570 }, data: { name: "AI Claim Scrub", agentId: "", agentName: "" } },
        { id: "deny_notify", type: "notification", position: { x: 460, y: 570 }, data: { name: "Notify Billing", channel: "alert", subject: "Eligibility Denied" } },
        { id: "review", type: "human_review", position: { x: 80, y: 690 }, data: { name: "Billing Review", reviewerRole: "billing" } },
        { id: "submit", type: "payer_action", position: { x: 80, y: 800 }, data: { name: "Submit Claim", payerAction: "claim_submit" } },
        { id: "audit", type: "compliance", position: { x: 260, y: 900 }, data: { name: "HIPAA Audit", checkType: "hipaa_audit" } },
        { id: "end", type: "wf_end", position: { x: 300, y: 1010 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "trigger" },
        { id: "e2", source: "trigger", target: "ehr_fetch" },
        { id: "e3", source: "ehr_fetch", target: "eligibility" },
        { id: "e4", source: "eligibility", target: "decision" },
        { id: "e5", source: "decision", target: "scrub_agent", label: "yes" },
        { id: "e6", source: "decision", target: "deny_notify", label: "no" },
        { id: "e7", source: "scrub_agent", target: "review" },
        { id: "e8", source: "review", target: "submit" },
        { id: "e9", source: "submit", target: "audit" },
        { id: "e10", source: "deny_notify", target: "audit" },
        { id: "e11", source: "audit", target: "end" },
      ],
      config: {},
    },
  },
  {
    id: "prior-auth-workflow",
    name: "Prior Authorization",
    description: "Deterministic PA workflow: gather docs, check requirements, submit, track, handle appeals",
    icon: Shield,
    color: "text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30",
    definition: {
      nodes: [
        { id: "start", type: "wf_start", position: { x: 300, y: 30 }, data: {} },
        { id: "trigger", type: "wf_trigger", position: { x: 260, y: 120 }, data: { name: "PA Request Created", triggerType: "clinical_event" } },
        { id: "ehr_fetch", type: "ehr_action", position: { x: 260, y: 230 }, data: { name: "Pull Clinical Docs", ehrAction: "query", resourceType: "DocumentReference" } },
        { id: "agent_review", type: "agent_ref", position: { x: 260, y: 340 }, data: { name: "AI Doc Review", agentId: "", agentName: "" } },
        { id: "clinician_sign", type: "human_review", position: { x: 260, y: 450 }, data: { name: "Clinician Sign-off", reviewerRole: "clinician", requireReason: true } },
        { id: "submit_pa", type: "payer_action", position: { x: 260, y: 560 }, data: { name: "Submit to Payer", payerAction: "prior_auth" } },
        { id: "wait", type: "timer", position: { x: 260, y: 670 }, data: { name: "Wait for Response", delayType: "fixed", delayMinutes: 1440, delayDescription: "24 hours" } },
        { id: "check_status", type: "payer_action", position: { x: 260, y: 780 }, data: { name: "Check PA Status", payerAction: "claim_status" } },
        { id: "notify_result", type: "notification", position: { x: 260, y: 890 }, data: { name: "Notify Staff", channel: "alert", subject: "PA Decision" } },
        { id: "end", type: "wf_end", position: { x: 300, y: 1000 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "trigger" },
        { id: "e2", source: "trigger", target: "ehr_fetch" },
        { id: "e3", source: "ehr_fetch", target: "agent_review" },
        { id: "e4", source: "agent_review", target: "clinician_sign" },
        { id: "e5", source: "clinician_sign", target: "submit_pa" },
        { id: "e6", source: "submit_pa", target: "wait" },
        { id: "e7", source: "wait", target: "check_status" },
        { id: "e8", source: "check_status", target: "notify_result" },
        { id: "e9", source: "notify_result", target: "end" },
      ],
      config: {},
    },
  },
  {
    id: "patient-onboarding",
    name: "Patient Onboarding",
    description: "New patient flow: verify insurance, collect intake, schedule appointment, send welcome",
    icon: Stethoscope,
    color: "text-violet-500 bg-violet-100 dark:bg-violet-900/30",
    definition: {
      nodes: [
        { id: "start", type: "wf_start", position: { x: 300, y: 30 }, data: {} },
        { id: "trigger", type: "wf_trigger", position: { x: 260, y: 120 }, data: { name: "New Patient Registered", triggerType: "clinical_event" } },
        { id: "ehr_create", type: "ehr_action", position: { x: 260, y: 230 }, data: { name: "Create EHR Record", ehrAction: "write", resourceType: "Patient" } },
        { id: "eligibility", type: "payer_action", position: { x: 260, y: 340 }, data: { name: "Verify Insurance", payerAction: "eligibility" } },
        { id: "intake_agent", type: "agent_ref", position: { x: 260, y: 450 }, data: { name: "AI Intake Assistant", agentId: "", agentName: "" } },
        { id: "schedule_notify", type: "notification", position: { x: 100, y: 560 }, data: { name: "Send Welcome Email", channel: "email", subject: "Welcome to our practice!" } },
        { id: "sms_remind", type: "notification", position: { x: 420, y: 560 }, data: { name: "Appointment Reminder", channel: "sms", body: "Your appointment is scheduled." } },
        { id: "compliance", type: "compliance", position: { x: 260, y: 670 }, data: { name: "Consent Verified", checkType: "consent_verify" } },
        { id: "end", type: "wf_end", position: { x: 300, y: 780 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "trigger" },
        { id: "e2", source: "trigger", target: "ehr_create" },
        { id: "e3", source: "ehr_create", target: "eligibility" },
        { id: "e4", source: "eligibility", target: "intake_agent" },
        { id: "e5", source: "intake_agent", target: "schedule_notify" },
        { id: "e6", source: "intake_agent", target: "sms_remind" },
        { id: "e7", source: "schedule_notify", target: "compliance" },
        { id: "e8", source: "sms_remind", target: "compliance" },
        { id: "e9", source: "compliance", target: "end" },
      ],
      config: {},
    },
  },
  {
    id: "referral-tracking",
    name: "Referral Management",
    description: "Track specialist referrals: schedule, fax records, follow up, close the loop",
    icon: FileText,
    color: "text-amber-500 bg-amber-100 dark:bg-amber-900/30",
    definition: {
      nodes: [
        { id: "start", type: "wf_start", position: { x: 300, y: 30 }, data: {} },
        { id: "trigger", type: "wf_trigger", position: { x: 260, y: 120 }, data: { name: "Referral Created", triggerType: "referral_event" } },
        { id: "ehr_fetch", type: "ehr_action", position: { x: 260, y: 230 }, data: { name: "Pull Patient Records", ehrAction: "query", resourceType: "DocumentReference" } },
        { id: "fax_records", type: "notification", position: { x: 260, y: 340 }, data: { name: "Fax to Specialist", channel: "fax", subject: "Referral Package" } },
        { id: "wait_appt", type: "timer", position: { x: 260, y: 450 }, data: { name: "Wait 7 Days", delayType: "fixed", delayMinutes: 10080, delayDescription: "7 days" } },
        { id: "followup_agent", type: "agent_ref", position: { x: 260, y: 560 }, data: { name: "AI Follow-up", agentId: "", agentName: "" } },
        { id: "staff_review", type: "human_review", position: { x: 260, y: 670 }, data: { name: "Staff Close Loop", reviewerRole: "admin" } },
        { id: "end", type: "wf_end", position: { x: 300, y: 780 }, data: {} },
      ],
      edges: [
        { id: "e1", source: "start", target: "trigger" },
        { id: "e2", source: "trigger", target: "ehr_fetch" },
        { id: "e3", source: "ehr_fetch", target: "fax_records" },
        { id: "e4", source: "fax_records", target: "wait_appt" },
        { id: "e5", source: "wait_appt", target: "followup_agent" },
        { id: "e6", source: "followup_agent", target: "staff_review" },
        { id: "e7", source: "staff_review", target: "end" },
      ],
      config: {},
    },
  },
];

interface WorkflowTemplateGalleryProps {
  onSelect: (name: string, definition: GraphDefinition) => void;
  onClose: () => void;
}

export function WorkflowTemplateGallery({ onSelect, onClose }: WorkflowTemplateGalleryProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-xl mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold">Workflow Templates</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Start with a pre-built healthcare process workflow</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm">
            Cancel
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 p-6">
          {WF_TEMPLATES.map((t) => (
            <button
              key={t.id}
              onClick={() => onSelect(t.name, t.definition)}
              className="group text-left rounded-lg border border-border p-4 hover:border-primary/40 hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", t.color)}>
                  <t.icon className="h-4.5 w-4.5" />
                </div>
                <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">{t.name}</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t.description}</p>
              <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
                <span>{t.definition.nodes.length} steps</span>
                <span>{t.definition.edges.length} connections</span>
              </div>
            </button>
          ))}
          {/* Blank workflow */}
          <button
            onClick={() =>
              onSelect("Untitled Workflow", {
                nodes: [
                  { id: "start", type: "wf_start", position: { x: 250, y: 50 }, data: {} },
                  { id: "end", type: "wf_end", position: { x: 250, y: 400 }, data: {} },
                ],
                edges: [],
                config: {},
              })
            }
            className="group text-left rounded-lg border border-dashed border-border p-4 hover:border-primary/40 transition-all"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 dark:bg-zinc-800">
                <GitBranch className="h-4.5 w-4.5" />
              </div>
              <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">Blank Workflow</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">Start from scratch with just Start and End nodes</p>
          </button>
        </div>
      </div>
    </div>
  );
}

export { WF_TEMPLATES };
